import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "../lib/supabase";

/**
 * UserContext
 * - Single source of truth for auth + caterer identity
 * - No timeouts
 * - No retries
 * - No race conditions
 * - Deterministic hydration
 */

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [userContext, setUserContext] = useState("caterer");

  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentCatererId, setCurrentCatererId] = useState(null);
  const [catererBlocked, setCatererBlocked] = useState(false);
  const [catererSuspect, setCatererSuspect] = useState(false);
  const [suspectReason, setSuspectReason] = useState(null);

  const [isUserLoading, setIsUserLoading] = useState(true);
  const [userError, setUserError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const hydrateUser = async () => {
      setIsUserLoading(true);
      setUserError(null);

      // 1. Get authenticated user
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (!mounted) return;

      if (authError || !user) {
        setCurrentUserId(null);
        setCurrentCatererId(null);
        setCatererBlocked(false);
        setIsUserLoading(false);
        return;
      }

      setCurrentUserId(user.id);

      // 2. Fetch caterer profile (if exists)
      const {
        data: caterer,
        error: catererError,
      } = await supabase
        .from("caterers")
        .select("id, is_blocked, is_suspect, suspect_reason")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!mounted) return;

      if (catererError) {
        console.error("UserContext: Caterer fetch failed", catererError);
        setCurrentCatererId(null);
        setCatererBlocked(false);
        setCatererSuspect(false);
        setSuspectReason(null);
        setUserError("Failed to load caterer profile");
      } else {
        setCurrentCatererId(caterer?.id ?? null);
        setCatererBlocked(!!caterer?.is_blocked);
        setCatererSuspect(!!caterer?.is_suspect);
        setSuspectReason(caterer?.suspect_reason ?? null);
      }

      setIsUserLoading(false);
    };

    // Initial load
    hydrateUser();

    // React to auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(() => {
      hydrateUser();
    });

    return () => {
      mounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const value = useMemo(() => {
    const isCaterer = userContext === "caterer";

    return {
      // UI context
      userContext,
      toggleUserContext: () =>
        setUserContext((prev) =>
          prev === "caterer" ? "customer" : "caterer"
        ),
      isCaterer,

      // Identity
      currentUserId,
      currentCatererId,
      catererBlocked,
      catererSuspect,
      suspectReason,

      // State
      isUserLoading,
      userError,
    };
  }, [
    userContext,
    currentUserId,
    currentCatererId,
    catererBlocked,
    catererSuspect,
    suspectReason,
    isUserLoading,
    userError,
  ]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}

export default UserContext;
